import { useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowDownRight } from 'lucide-react';
import { HeroScene } from '@/components/three/HeroScene';
import { SplitLines } from '@/components/SplitLines';
import { STATS, BRAND } from '@/data/content';
import { scrollToId } from '@/lib/smoothScroll';
import { usePerfTier } from '@/lib/usePerfTier';

gsap.registerPlugin(ScrollTrigger);

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ started }) => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const progressRef = useRef(0);
  const { isMobile, reduced, dpr, particles } = usePerfTier();

  useLayoutEffect(() => {
    const isShort = window.innerHeight < 780;
    const pin = !isMobile && !reduced && !isShort;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: pin ? '+=120%' : 'bottom top',
        pin,
        onUpdate: (self) => {
          progressRef.current = self.progress;
          if (pin && contentRef.current) {
            const f = Math.min(1, self.progress / 0.55);
            gsap.set(contentRef.current, { opacity: 1 - f, y: -90 * f });
          }
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isMobile, reduced]);

  return (
    <section ref={sectionRef} id="home" data-testid="hero-section" className="relative min-h-screen [@media(min-width:1024px)_and_(min-height:780px)]:h-screen [@media(min-width:1024px)_and_(min-height:780px)]:overflow-hidden bg-void">
      <div className="absolute inset-0">
        <HeroScene progressRef={progressRef} isMobile={isMobile} reduced={reduced} dpr={dpr} particles={particles} />
      </div>

      {/* readability scrim */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-void/80 via-void/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-t from-void to-transparent" />

      <div ref={contentRef} className="relative z-10 min-h-screen [@media(min-width:1024px)_and_(min-height:780px)]:h-full [@media(min-width:1024px)_and_(min-height:780px)]:min-h-0 flex flex-col justify-center section-pad pt-28 pb-40 [@media(min-width:1024px)_and_(min-height:780px)]:py-0 pointer-events-none">
        
        <div className="flex w-full justify-between items-center gap-10">
          {/* Left: Text Content */}
          <div className="flex-1">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={started ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
              className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-brand-gold mb-6"
            >
              {BRAND.legal} × {BRAND.partner}
            </motion.p>

            <h1 className="font-clash font-semibold text-ink tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
              <SplitLines
                start={started}
                delay={0.35}
                lines={[
                  "Building Tomorrow's",
                  'Communities with',
                  <span key="g" className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold via-brand-green to-brand-royal">Trust &amp; Excellence</span>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={started ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
              className="mt-7 max-w-xl text-sm md:text-base text-ink/70 leading-relaxed"
            >
              Premium residential communities, plotted developments, villas and investment
              destinations across Telangana — crafted by Hyderabad's trusted developers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={started ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4 pointer-events-auto"
            >
              <motion.button
                data-testid="hero-explore-projects-btn"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => scrollToId('projects')}
                className="bg-brand-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_35px_rgba(201,151,27,0.45)] transition-shadow duration-300"
              >
                Explore Projects
              </motion.button>
              <motion.button
                data-testid="hero-contact-us-btn"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => scrollToId('contact')}
                className="glass text-ink text-sm font-medium px-8 py-4 rounded-full hover:border-brand-gold/50 transition-colors duration-300"
              >
                Contact Us
              </motion.button>
            </motion.div>
          </div>

          {/* Right: Brand Logos (hidden on mobile) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={started ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="hidden xl:flex flex-row gap-12 items-center mr-10 pointer-events-auto"
          >
            <motion.img 
              whileHover={{ scale: 1.05 }}
              src="/assets/logo.png" 
              alt={BRAND.legal} 
              className="h-28 object-contain mix-blend-multiply drop-shadow-sm transition-transform duration-300" 
            />
            
            <div className="text-brand-gold font-light text-7xl font-clash -translate-y-2">
              ×
            </div>
            
            <motion.img 
              whileHover={{ scale: 1.05 }}
              src="/assets/sasog.png" 
              alt={BRAND.partner} 
              className="h-28 object-contain mix-blend-multiply drop-shadow-sm transition-transform duration-300" 
            />
          </motion.div>
        </div>
      </div>

      {/* bottom stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={started ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1, delay: 1.4, ease: EASE }}
        className="absolute bottom-0 inset-x-0 z-10 section-pad pb-8 flex items-end justify-between gap-6"
      >
        <div className="flex gap-4 sm:gap-8 md:gap-14" data-testid="hero-stats-strip">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-clash font-semibold text-2xl md:text-4xl text-ink leading-none">
                {s.value}<span className="text-brand-gold">{s.suffix}</span>
              </p>
              <p className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-ink/55">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="hidden md:flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-ink/55 rotate-90 origin-center translate-y-[-14px]">Scroll</span>
          <div className="w-px h-14 bg-ink/15 overflow-hidden">
            <div className="scroll-hint-line w-px h-full bg-brand-gold" />
          </div>
          <ArrowDownRight size={14} className="text-brand-gold" />
        </div>
      </motion.div>
    </section>
  );
};
